import { Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { FaUser } from 'react-icons/fa';
import { Icon, Text, Tooltip } from '@/components/v4';

const Placeholder = ({ children = '—' }) => (
    <Text color="gray.500" fontStyle="italic">
        {children}
    </Text>
);

/** Trunk name with the Overstamp beneath it, highlighted when it differs. */
export const TrunkCell = ({ data }) => {
    const { darkMode } = useSelector((state) => state.settings);
    if (!data.trunkID) return <Placeholder>None</Placeholder>;

    const overstampDiffers = !!data.overstamp && data.overstamp !== data.e164;
    let overstampColor = 'gray.500';
    if (overstampDiffers)
        overstampColor = darkMode ? 'orange.300' : 'orange.600';

    return (
        <Stack spacing={0} lineHeight="short" justify="center" minW={0}>
            <Text fontWeight="medium" isTruncated>
                {data.trunkName}
            </Text>
            <Text
                fontSize="xs"
                color={overstampColor}
                fontWeight={overstampDiffers ? 'semibold' : 'normal'}
                isTruncated>
                Overstamp: {data.overstamp || '—'}
            </Text>
        </Stack>
    );
};

/** A user icon when a Teams user has the number; the column has no names. */
export const UserCell = ({ data }) => {
    const { darkMode } = useSelector((state) => state.settings);
    if (!data.userAssigned) return null;
    return (
        <Tooltip
            label="Assigned to a user"
            hasArrow
            placement="top"
            // The icon doesn't take a ref, so the tooltip wraps it
            shouldWrapChildren>
            <Icon
                as={FaUser}
                aria-label="Assigned to a user"
                boxSize={3.5}
                color={darkMode ? 'gray.300' : 'gray.600'}
            />
        </Tooltip>
    );
};

/** A plain text value, or a dash when there is none. */
export const TextCell = ({ value }) =>
    value ? <Text isTruncated>{value}</Text> : <Placeholder />;

/**
 * A value in a column's header filter list, with how many numbers have it.
 * Counts come from the grid context so they follow the loaded rows without
 * rebuilding the columns.
 */
export const FilterValueWithCount = ({
    value,
    valueFormatted,
    column,
    context,
}) => {
    const count = context?.filterCounts?.[column.getColId()]?.get(value);
    const label = valueFormatted ?? value;
    return count == null ? label : `${label} (${count.toLocaleString()})`;
};
